import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useI18n } from "../i18n/LanguageContext";
import { Menu, X } from "lucide-react";
import { LogoLockup } from "./Logo";

const LangBtn = ({ code }) => {
  const { lang, setLang } = useI18n();
  const active = lang === code;
  return (
    <button
      type="button"
      onClick={() => setLang(code)}
      data-testid={`lang-toggle-${code}`}
      className={`label-tech px-2.5 py-1 transition-colors ${
        active ? "text-white" : "text-white/40 hover:text-white/80"
      }`}
    >
      {code.toUpperCase()}
    </button>
  );
};

export const Navbar = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-home" },
    { to: "/services", label: t.nav.services, testid: "nav-services" },
    { to: "/calculateurs", label: t.nav.calculators, testid: "nav-calculators" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
  ];

  return (
    <header
      className="sticky top-0 z-40 w-full bg-[#161618]/85 backdrop-blur-xl border-b border-white/10"
      data-testid="site-navbar"
    >
      <div className="px-5 md:px-10 lg:px-16">
        <div className="flex h-16 md:h-20 items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <LogoLockup size={26} />
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `label-tech transition-colors ${
                    isActive ? "text-white" : "text-white/50 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center border border-white/15">
              <LangBtn code="fr" />
              <span className="w-px h-4 bg-white/15" />
              <LangBtn code="de" />
              <span className="w-px h-4 bg-white/15" />
              <LangBtn code="en" />
            </div>

            <Link
              to="/contact"
              data-testid="nav-cta"
              className="hidden md:inline-flex items-center gap-2 bg-flame text-white label-tech px-5 py-3 hover:brightness-110 transition-all"
            >
              {t.nav.cta}
              <span className="hover-arrow">→</span>
            </Link>

            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden p-2 border border-white/15 text-white"
              aria-label="menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#161618]" data-testid="nav-mobile-panel">
          <div className="px-5 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `label-tech ${isActive ? "text-white" : "text-white/50"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center border border-white/15 w-fit">
              <LangBtn code="fr" />
              <span className="w-px h-4 bg-white/15" />
              <LangBtn code="de" />
              <span className="w-px h-4 bg-white/15" />
              <LangBtn code="en" />
            </div>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="bg-flame text-white label-tech px-5 py-3 inline-flex items-center justify-between"
            >
              {t.nav.cta}
              <span>→</span>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
